/**
 * useAssistantChat — thin consumer of <AssistantChatProvider>.
 *
 * Pulls the shared Chat + AssistantTransport from context and hands the Chat
 * to Vercel SDK v6's useChat({ chat }) so every consumer reads and writes the
 * same conversation state. Returns the curated subset the assistant UI
 * actually uses.
 *
 * Curated surface:
 *   - sendMessage, status, messages, error, clearError — needed for chat UX
 *   - addToolApprovalResponse — wired to AssistantTransport.sendApprovalResponse
 *   - stop — wired to useChat's stop AND to AssistantTransport.clearRunId(chatId)
 *   - regenerate is INTENTIONALLY OMITTED — cortex has no per-turn regenerate
 *     semantics, and exposing it would let UI fire malformed envelopes.
 *
 * The hook takes ZERO arguments — all configuration lives on the provider.
 * Tests that need a custom getContext / callbacks should wrap the consumer in
 * <AssistantChatProvider getContext={...} onTurnComplete={...} />.
 */

import { useCallback } from 'react';
import { useChat, type UseChatHelpers } from '@ai-sdk/react';
import type { UIMessage } from 'ai';
import { useAssistantChatContext } from '../components/assistant-chat-provider';

// Note: 'error' field comes from UseChatHelpers (the @ai-sdk/react React
// wrapper) directly, not from AbstractChat — so it's part of this Pick
// even though the other fields delegate through AbstractChat.
export interface UseAssistantChatReturn
  extends Pick<
    UseChatHelpers<UIMessage>,
    'sendMessage' | 'stop' | 'status' | 'messages' | 'error' | 'clearError'
  > {
  /** Resolve a pending tool-approval-request. */
  addToolApprovalResponse: (payload: {
    id: string;
    approved: boolean;
    reason?: string;
  }) => void;
  /** The currently-active runId for this chat, if any. */
  runId: string | undefined;
}

export function useAssistantChat(): UseAssistantChatReturn {
  const { chat, transport, chatId } = useAssistantChatContext();

  const helpers = useChat<UIMessage>({ chat });

  const addToolApprovalResponse = useCallback(
    (payload: { id: string; approved: boolean; reason?: string }) => {
      // Diagnostic: log every approval-response invocation so we can trace
      // duplicate clicks, missing transport sends, and SDK state transitions.
      console.info(
        '[useAssistantChat] addToolApprovalResponse',
        { chatId, id: payload.id, approved: payload.approved },
      );
      transport.sendApprovalResponse(chatId, {
        approvalId: payload.id,
        approved: payload.approved,
      });
      // Also notify useChat so the per-tool state in messages[] reflects the response.
      void helpers.addToolApprovalResponse({
        id: payload.id,
        approved: payload.approved,
        reason: payload.reason,
      });
    },
    [transport, chatId, helpers],
  );

  // Wrap stop so that it also clears the AssistantTransport's runId (and the
  // in-flight running flag). Without this, a follow-up sendMessage after a stop
  // would reuse the existing runId and the chat would stay marked running,
  // blocking the next send. Every turn is a fresh KIND_AGENT_RUN (M1c).
  const stop = useCallback((): Promise<void> => {
    const result = helpers.stop();
    transport.clearRunId(chatId);
    return result;
  }, [helpers, transport, chatId]);

  return {
    sendMessage: helpers.sendMessage,
    stop,
    status: helpers.status,
    messages: helpers.messages,
    error: helpers.error,
    clearError: helpers.clearError,
    addToolApprovalResponse,
    runId: transport.getRunId(chatId),
  };
}
